import React, { useEffect, useMemo, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  TextInput,
  TextareaInput,
  CheckboxField,
  type OptionItem,
} from "@/components/formfields";
import { SelectDropdown } from "@/components/formfields/SelectDropdown";
import type { ProjectDetail, ProjectPayload } from "@/interface/profile";
import type { SkillDetail } from "@/interface/masterManagement";
import { useCreateProject, useUpdateProject } from "../hooks/useProjects";
import { useInfiniteSkillList } from "@/pages/master-management/hooks/useSkill";
import { useIndustryList } from "@/pages/master-management/hooks/useIndustry";
import { useProfileDetail } from "../hooks/useProfile";
import type { IndustryDetail, PaginationResponse } from "@/interface";

const getTodayDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const projectSchema = z
  .object({
    project_name: z
      .string()
      .trim()
      .min(1, "Project Name is required")
      .max(255, "Project Name cannot exceed 255 characters"),
    role: z.string().trim().max(255, "Role cannot exceed 255 characters"),
    client_organization: z
      .string()
      .trim()
      .max(255, "Client Organization cannot exceed 255 characters"),
    start_date: z.string().trim().optional(),
    end_date: z.string().trim().optional(),
    is_current: z.boolean().default(false),
    team_size: z
      .union([z.number(), z.string(), z.undefined(), z.null()])
      .optional(),
    description: z.string().trim().min(1, "Description is required"),
    key_responsibilities: z
      .string()
      .trim()
      .min(1, "Key Responsibilities are required"),
    contributions: z.string().trim().min(1, "Contributions are required"),
    achievements: z.string().trim().min(1, "Achievements are required"),
    industry_name: z.string().trim().optional(),
    technologies: z.array(z.number()).default([]),
  })
  .superRefine((data, ctx) => {
    const todayStr = getTodayDateString();

    if (
      data.start_date &&
      data.start_date.trim() &&
      data.start_date > todayStr
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Start date cannot be in the future",
        path: ["start_date"],
      });
    }

    if (!data.is_current && data.end_date && data.end_date.trim()) {
      if (data.end_date > todayStr) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date cannot be in the future",
          path: ["end_date"],
        });
      }
    }

    if (
      data.start_date &&
      data.end_date &&
      data.start_date.trim() &&
      data.end_date.trim() &&
      !data.is_current
    ) {
      const start = new Date(data.start_date);
      const end = new Date(data.end_date);

      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start >= end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Start date must be before End date",
          path: ["end_date"],
        });
      }
    }
  });

export type ProjectFormValues = z.infer<typeof projectSchema>;

interface ProjectFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: ProjectDetail | null;
  profileId?: number | string;
}

export const ProjectFormModal: React.FC<ProjectFormModalProps> = ({
  isOpen,
  onClose,
  editData,
  profileId,
}) => {
  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject();

  const { data: profileResponse } = useProfileDetail();
  const selfId = profileResponse?.data?.id;
  const targetUserId = profileId ?? selfId ?? editData?.user_profile;

  const { data: industryData, isLoading: isIndustryLoading } =
    useIndustryList();
  const industryOptions = useMemo(() => {
    const list = Array.isArray(industryData?.data)
      ? industryData.data
      : (industryData?.data as PaginationResponse<IndustryDetail>)?.results ||
        [];
    return list.map((ind: IndustryDetail) => ({
      label: ind.industry_name || "",
      value: String(ind.id),
    }));
  }, [industryData]);

  const [skillSearch, setSkillSearch] = useState<string>("");
  const [debouncedSkillSearch, setDebouncedSkillSearch] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSkillSearch(skillSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [skillSearch]);

  const {
    data: skillData,
    fetchNextPage: fetchNextSkillPage,
    hasNextPage: hasNextSkillPage,
    isFetchingNextPage: isFetchingNextSkillPage,
    isLoading: isSkillsLoading,
  } = useInfiniteSkillList({
    page: 1,
    page_size: 10,
    search: debouncedSkillSearch || undefined,
  });

  const skillsList = useMemo(() => {
    if (!skillData?.pages) return [];
    return skillData.pages.flatMap((page) => {
      const items = Array.isArray(page?.data)
        ? page.data
        : page?.data?.results || [];
      return items;
    });
  }, [skillData]);

  const skillOptions: OptionItem[] = useMemo(() => {
    return skillsList
      .map((s: SkillDetail) => ({
        label: s.skill_name || "",
        value: String(s.id),
      }))
      .filter((opt: OptionItem) => Boolean(opt.label));
  }, [skillsList]);

  // Maintain historical skills map so chip labels are retained across remote searches
  const skillCacheRef = useRef<Map<number, string>>(new Map());

  useEffect(() => {
    skillsList.forEach((s: SkillDetail) => {
      if (s?.id && s?.skill_name) {
        skillCacheRef.current.set(Number(s.id), s.skill_name);
      }
    });
    if (editData && Array.isArray(editData.technologies)) {
      editData.technologies.forEach(
        (s: number | string | { id?: number | string; skill_name?: string }) => {
          if (typeof s === "object" && s?.id && s?.skill_name) {
            skillCacheRef.current.set(Number(s.id), s.skill_name);
          }
        },
      );
    }
  }, [skillsList, editData]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      project_name: "",
      role: "",
      client_organization: "",
      start_date: "",
      end_date: "",
      is_current: false,
      team_size: "",
      description: "",
      key_responsibilities: "",
      contributions: "",
      achievements: "",
      industry_name: "",
      technologies: [],
    },
  });

  const isCurrent = watch("is_current");
  const selectedTechIds = watch("technologies") || [];
  const selectedIndustry = watch("industry_name") || "";

  const handleAddSkill = (skillIdStr: string) => {
    const numId = Number(skillIdStr);
    if (isNaN(numId) || numId === 0) return;
    if (!selectedTechIds.includes(numId)) {
      const updated = [...selectedTechIds, numId];
      setValue("technologies", updated, { shouldValidate: true });
    }
    setSkillSearch("");
    setDebouncedSkillSearch("");
  };

  const handleRemoveSkill = (skillIdToRemove: number) => {
    const updated = selectedTechIds.filter((id) => id !== skillIdToRemove);
    setValue("technologies", updated, { shouldValidate: true });
  };

  const getSkillName = (id: number): string => {
    const cached = skillCacheRef.current.get(id);
    if (cached) return cached;
    const found = skillsList.find((s) => Number(s.id) === id);
    if (found) return found.skill_name;
    if (editData && Array.isArray(editData.technologies)) {
      const item = editData.technologies.find(
        (s) => (typeof s === "object" ? Number(s.id) : Number(s)) === id,
      );
      if (item && typeof item === "object" && item.skill_name)
        return item.skill_name;
    }
    return `Skill #${id}`;
  };

  useEffect(() => {
    if (isOpen) {
      setSkillSearch("");
      setDebouncedSkillSearch("");
      if (editData) {
        const techIds: number[] = Array.isArray(editData.technologies)
          ? editData.technologies
              .map((s) => Number(s.id))
              .filter((n) => !isNaN(n))
          : [];

        const industryVal = editData.industry?.id
          ? String(editData.industry.id)
          : "";

        reset({
          project_name: editData.project_name || "",
          role: editData.role || "",
          client_organization: editData.client_organization || "",
          start_date: editData.start_date || "",
          end_date: editData.end_date || "",
          is_current: editData.is_current ?? false,
          team_size: editData.team_size ?? "",
          description: editData.description || "",
          key_responsibilities: editData.key_responsibilities || "",
          contributions: editData.contributions || "",
          achievements: editData.achievements || "",
          industry_name: industryVal,
          technologies: techIds,
        });
      } else {
        reset({
          project_name: "",
          role: "",
          client_organization: "",
          start_date: "",
          end_date: "",
          is_current: false,
          team_size: "",
          description: "",
          key_responsibilities: "",
          contributions: "",
          achievements: "",
          industry_name: "",
          technologies: [],
        });
      }
    }
  }, [isOpen, editData, reset]);

  const onSubmit = async (values: ProjectFormValues) => {
    try {
      const parsedTeamSize = values.team_size ? Number(values.team_size) : null;

      const parsedIndustry = values.industry_name
        ? isNaN(Number(values.industry_name))
          ? values.industry_name
          : Number(values.industry_name)
        : null;

      const payload: ProjectPayload = {
        project_name: values.project_name,
        role: values.role,
        client_organization: values.client_organization,
        start_date: values.start_date || null,
        end_date: values.is_current ? null : values.end_date || null,
        is_current: values.is_current,
        team_size: parsedTeamSize,
        description: values.description,
        key_responsibilities: values.key_responsibilities,
        contributions: values.contributions,
        achievements: values.achievements,
        user_profile: targetUserId,
        industry_name: parsedIndustry,
        technologies: values.technologies,
      };

      if (editData) {
        await updateMutation.mutateAsync({ id: editData.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Handled by query mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle>{editData ? "Edit Project" : "Add Project"}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50 dark:bg-slate-900/50">
            <TextInput
              control={control}
              name="project_name"
              label="Project Name"
              placeholder="e.g. NextGen E-Commerce Portal"
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                control={control}
                name="role"
                label="Your Role"
                placeholder="e.g. Lead Frontend Developer"
                required
              />
              <SelectDropdown
                label="Industry"
                placeholder={
                  isIndustryLoading
                    ? "Loading industries..."
                    : "Select industry"
                }
                options={industryOptions}
                onChange={(val) =>
                  setValue("industry_name", String(val), {
                    shouldValidate: true,
                  })
                }
                value={selectedIndustry}
                isSearchable
              />
            </div>
            <TextInput
              control={control}
              name="client_organization"
              label="Client Organization"
              placeholder="e.g. Acme Corporation"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                control={control}
                name="team_size"
                type="number"
                label="Team Size"
                placeholder="e.g. 10"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                control={control}
                name="start_date"
                type="date"
                label="Start Date"
                max={getTodayDateString()}
              />
              {!isCurrent && (
                <TextInput
                  control={control}
                  name="end_date"
                  type="date"
                  label="End Date"
                  max={getTodayDateString()}
                />
              )}
            </div>

            <CheckboxField
              control={control}
              name="is_current"
              label="This is a current project"
            />

            <div className="space-y-2">
              <SelectDropdown
                label="Technologies Used"
                placeholder={
                  isSkillsLoading ? "Loading skills..." : "Select skill..."
                }
                options={skillOptions}
                onChange={(val) => handleAddSkill(String(val))}
                value=""
                isSearchable
                onSearch={(query) => setSkillSearch(query)}
                onMenuScrollToBottom={() => {
                  if (hasNextSkillPage && !isFetchingNextSkillPage) {
                    fetchNextSkillPage();
                  }
                }}
                hasMore={hasNextSkillPage}
                isLoadingMore={isFetchingNextSkillPage}
              />

              {selectedTechIds.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {selectedTechIds.map((id) => (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50 transition-all shadow-sm"
                    >
                      {getSkillName(id)}
                      <button
                        type="button"
                        onClick={() => handleRemoveSkill(id)}
                        className="text-purple-500 hover:text-purple-700 dark:hover:text-purple-200 p-0.5 rounded-full hover:bg-purple-200/60 dark:hover:bg-purple-800/60 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <TextareaInput
              control={control}
              name="description"
              label="Project Description"
              placeholder="Describe the overall project..."
              rows={6}
              required
            />

            <TextareaInput
              control={control}
              name="key_responsibilities"
              label="Key Responsibilities"
              placeholder="Outline your primary duties and responsibilities..."
              rows={3}
              required
            />

            <TextareaInput
              control={control}
              name="contributions"
              label="Contributions"
              placeholder="Highlight your key technical contributions..."
              rows={3}
              required
            />

            <TextareaInput
              control={control}
              name="achievements"
              label="Achievements"
              placeholder="List major impacts and outcomes achieved..."
              rows={3}
              required
            />
          </div>

          <DialogFooter className="p-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending
                ? "Saving..."
                : editData
                  ? "Save Changes"
                  : "Add Project"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectFormModal;
